import type { LitElement, PropertyValues, TemplateResult } from 'lit'
import { ContextConsumer } from '@lit/context'
import { html, nothing } from 'lit'
import { property } from 'lit/decorators.js'
import { fieldContext } from '../../field/field-context.js'
import { SlotController } from '../controllers/SlotController.js'
import { injectLegacyLightDomStyles } from '../light-dom-styles.js'
import fieldControlLightStyle from '../styles/FieldControlLight.css'
import '../../visually-hidden/VisuallyHidden.js'

type Constructor<T = Record<string, unknown>> = new (...args: any[]) => T

export declare class FieldControlMixinInterface {
  label: string
  hint?: string
  hintBelow: boolean
  hideLabel: boolean
  error?: string
  required: boolean
  hideRequired: boolean

  protected inputId: string
  protected errorId: string
  protected hintId: string
  protected labelSlot: SlotController
  protected hintSlot: SlotController
  protected errorSlot: SlotController

  /** True while the control is inside a `<nord-field>` (reactive). */
  readonly insideField: boolean

  /** `disabled` merged with the field's disabled (either activates it). */
  protected get effectiveDisabled(): boolean
  /** `invalid` merged from the field and the control's own invalid state. */
  protected get effectiveInvalid(): boolean
  /** The control's own invalid state. Defaults to `hasError`; override on controls with an `invalid` prop. */
  protected get ownInvalid(): boolean

  protected get hasHint(): boolean
  protected get hasError(): boolean
  protected get isHintBelow(): boolean
  /** When true, a label click focuses the control instead of activating it. */
  protected get labelFocusOnly(): boolean

  /** `aria-label` for the inner focusable — shadow-DOM controls inside a field only. */
  protected get controlAriaLabel(): string | undefined
  /** `aria-labelledby` for the inner focusable — light-DOM controls inside a field only. */
  protected get controlAriaLabelledby(): string | undefined
  /** `aria-describedby` for the inner focusable — field ids inside a field, built-in ids standalone. */
  protected get controlAriaDescribedby(): string | undefined
  /** Actual Field helper elements for cross-root ARIA element reflection. */
  protected get controlAriaDescribedByElements(): readonly HTMLElement[] | undefined
  /** `aria-invalid` for the inner focusable. */
  protected get controlAriaInvalid(): 'true' | undefined

  /** Re-requests Field context after a late-upgrading provider becomes available. */
  protected requestFieldContext(): void

  protected renderLabel(additionalContent?: TemplateResult): TemplateResult | typeof nothing
  protected renderHint(): TemplateResult | typeof nothing
  protected renderError(): TemplateResult | typeof nothing
  protected getDescribedBy(): string | undefined
  protected getInvalid(): 'true' | undefined
}

/**
 * Gives a form control its built-in field UI (label, hint, error, required
 * indicator) AND makes it a consumer of the [Field](/components/field/) context.
 *
 * - **Standalone:** the control renders its own label/hint/error from these props.
 * - **Inside `<nord-field>`:** the control suppresses its built-in field UI and
 *   the field parts take over. The control reads its accessible name,
 *   description and invalid/disabled state from the field context.
 *
 * The control owns `required`; it publishes it up so `<nord-field-label>` can
 * render the indicator.
 */
export function FieldControlMixin<T extends Constructor<LitElement>>(superClass: T) {
  /**
   * @slot label - Use when a label requires more than plain text.
   * @slot hint - Optional slot that holds hint text for the control.
   * @slot error - Optional slot that holds error text for the control.
   */
  class FieldControlElement extends superClass {
    protected labelSlot = new SlotController(this, 'label')
    protected errorSlot = new SlotController(this, 'error')
    protected hintSlot = new SlotController(this, 'hint')

    protected inputId = 'input'
    protected errorId = 'error'
    protected hintId = 'hint'

    /**
     * Label for the control. Ignored when the control is wrapped in a
     * `<nord-field>`, which provides the label via `<nord-field-label>`.
     */
    @property({ reflect: true }) label: string = ''

    /**
     * Optional hint text shown with the control. Ignored inside a `<nord-field>`,
     * which provides it via `<nord-field-description>`.
     */
    @property({ reflect: true }) hint?: string

    /**
     * Renders the hint below the control and any error instead of below the label.
     */
    @property({ reflect: true, type: Boolean, attribute: 'hint-below' }) hintBelow = false

    /**
     * Visually hide the label, but still expose it to assistive technologies.
     */
    @property({ reflect: true, type: Boolean, attribute: 'hide-label' }) hideLabel = false

    /**
     * Optional error shown with the control. Ignored inside a `<nord-field>`,
     * which provides it via `<nord-field-error>`.
     */
    @property({ reflect: true }) error?: string

    /**
     * Determines whether the control is required or not.
     * A required control is announced as such to assistive technology and, inside
     * a `<nord-field>`, drives the required indicator on the `<nord-field-label>`.
     * When using this property you need to also set “novalidate” attribute on a form element to prevent browser from displaying its own validation errors.
     */
    @property({ reflect: true, type: Boolean }) required = false

    /**
     * Visually hide the required indicator, but still expose the required state
     * to assistive technologies.
     */
    @property({ reflect: true, type: Boolean, attribute: 'hide-required' }) hideRequired = false

    /** Reactive consumer of the enclosing Field's context. */
    private fieldConsumer = new ContextConsumer(this, {
      context: fieldContext,
      subscribe: true,
      // Re-publish `required` whenever the field context resolves or changes, so
      // a control that upgraded before its Field still surfaces its indicator.
      callback: () => this.publishRequired(),
    })

    protected requestFieldContext() {
      this.fieldConsumer.hostConnected()
    }

    get insideField() {
      return this.fieldConsumer.value !== undefined
    }

    protected get effectiveDisabled() {
      // Every form control exposes `disabled` (via InputMixin or its own prop),
      // but we don't constrain the superclass to it so control-only components
      // can keep their own. Read it loosely.
      return (this.fieldConsumer.value?.disabled ?? false) || Boolean((this as { disabled?: boolean }).disabled)
    }

    protected get ownInvalid() {
      return this.hasError
    }

    protected get effectiveInvalid() {
      return (this.fieldConsumer.value?.invalid ?? false) || this.ownInvalid
    }

    connectedCallback() {
      super.connectedCallback()
      this.publishRequired()
    }

    disconnectedCallback() {
      // Stop marking the field required when the control leaves it.
      this.fieldConsumer.value?.setRequired(false)
      // ContextConsumer intentionally retains its last value when no replacement
      // provider answers on reconnect. Clear derived Field state before moving a
      // control to standalone content while preserving its own public props.
      this.fieldConsumer.value = undefined
      this.requestUpdate()
      super.disconnectedCallback()
    }

    /**
     * Whether the control renders in light DOM. True both when the render root is
     * the host itself and when it is a light-DOM child wrapper (e.g. the combobox
     * renders into a `.n-render-root` div so it can keep slotted children). Only a
     * real `ShadowRoot` counts as shadow DOM.
     */
    protected get isLightDom() {
      return !(this.renderRoot instanceof ShadowRoot)
    }

    protected firstUpdated(changed: PropertyValues) {
      super.firstUpdated(changed)
      // Light-DOM controls render the field UI in light DOM, so they need the field
      // styles injected (scoped under data-field-control so they don't leak).
      // Shadow-DOM controls already bundle FormField.css.
      if (this.isLightDom) {
        this.setAttribute('data-field-control', '')
        injectLegacyLightDomStyles(this, fieldControlLightStyle, 'n-field-control-light')
      }
    }

    protected updated(changed: PropertyValues) {
      super.updated(changed)
      if (changed.has('required')) {
        this.publishRequired()
      }
    }

    private publishRequired() {
      this.fieldConsumer.value?.setRequired(this.required)
    }

    /**
     * When true, clicking the label focuses the control (showing the focus ring)
     * instead of activating it — for popup-trigger controls (e.g. Date Range
     * Picker) where a native `<label for>` would otherwise open the popup on
     * click. Keyboard Space/Enter on the focused control still activates it.
     */
    protected get labelFocusOnly(): boolean {
      return false
    }

    private handleLabelFocus = (e: Event) => {
      // Don't let the label forward an activating click to the control; focus it
      // (with the keyboard focus ring) instead.
      e.preventDefault()
      ;(this as unknown as { focus: (o?: FocusOptions) => void }).focus?.({ focusVisible: true } as FocusOptions)
    }

    protected renderLabel(additionalContent?: TemplateResult) {
      // Inside a Field, the <nord-field-label> is the only visible label.
      if (this.insideField) {
        return nothing
      }

      const label = html`
        <label for=${this.inputId} @click=${this.labelFocusOnly ? this.handleLabelFocus : nothing}>
          <slot name="label">${this.label}</slot
          ><span ?hidden=${!this.required || this.hideRequired} aria-hidden="true" class="n-required">*</span>
          ${additionalContent}
        </label>
      `

      if (this.hideLabel) {
        return html`
          <nord-visually-hidden>
            ${label}
            ${this.isHintBelow ? nothing : this.renderHint()}
          </nord-visually-hidden>
        `
      }

      return html`
        <div class="n-label-container">
          ${label}
          ${this.isHintBelow ? nothing : this.renderHint()}
        </div>
      `
    }

    protected renderHint() {
      if (this.insideField) {
        return nothing
      }
      return html`
        <div class="n-caption n-hint" id=${this.hintId} ?hidden=${!this.hasHint}>
          <slot name="hint">${this.hint}</slot>
        </div>
      `
    }

    protected renderError() {
      if (this.insideField) {
        return nothing
      }
      return html`
        <div class="n-caption n-error" id=${this.errorId} role="alert" ?hidden=${!this.hasError}>
          <slot name="error">${this.error}</slot>
        </div>
      `
    }

    protected getDescribedBy() {
      const { hasHint, hasError } = this

      if (hasHint && hasError) {
        if (this.isHintBelow) {
          return `${this.errorId} ${this.hintId}`
        }
        return `${this.hintId} ${this.errorId}`
      }
      if (hasHint) {
        return this.hintId
      }
      if (hasError) {
        return this.errorId
      }

      return undefined
    }

    protected getInvalid() {
      return this.hasError ? 'true' : undefined
    }

    protected get controlAriaLabel() {
      // Shadow-DOM controls can't reference the light-DOM label id, so they take
      // the label text as an aria-label instead. A consumer-set host `aria-label`
      // is honoured as a fallback (and lets a composing parent name the inner control).
      const fromContext = this.insideField && !this.isLightDom ? this.fieldConsumer.value?.labelText : undefined
      return fromContext ?? this.getAttribute('aria-label') ?? undefined
    }

    protected get controlAriaLabelledby() {
      // Only light-DOM controls can resolve a light-DOM IDREF across the tree.
      if (!this.isLightDom) {
        return undefined
      }
      const fromContext = this.insideField ? this.fieldConsumer.value?.labelId : undefined
      return fromContext ?? this.getAttribute('aria-labelledby') ?? undefined
    }

    protected get controlAriaDescribedby() {
      const fromField = this.insideField ? this.fieldConsumer.value?.describedById : this.getDescribedBy()
      // Merge a consumer-set host `aria-describedby` (e.g. a composing parent).
      return [fromField, this.getAttribute('aria-describedby')].filter(Boolean).join(' ') || undefined
    }

    protected get controlAriaDescribedByElements() {
      return this.insideField && !this.isLightDom
        ? this.fieldConsumer.value?.describedByElements ?? []
        : undefined
    }

    protected get controlAriaInvalid() {
      return this.effectiveInvalid ? 'true' : undefined
    }

    protected get hasHint() {
      return !this.insideField && (Boolean(this.hint) || this.hintSlot.hasContent)
    }

    protected get hasError() {
      return !this.insideField && (Boolean(this.error) || this.errorSlot.hasContent)
    }

    protected get isHintBelow() {
      return this.hintBelow
    }
  }

  return FieldControlElement as unknown as Constructor<FieldControlMixinInterface> & T
}
